import { useState, useMemo } from 'react';
import { cluesFromGrid } from '@org/nonogram-engine';
import { useGameSettings } from '../store';
import { playClick, playToggle } from '../sounds';
import { ParallaxBg } from './ParallaxBg';
import { Title, Muted, Button, Divider, Toggle, MultiToggle } from './ui';
import { BoardGrid } from './BoardGrid';
import { CellState } from '../types';
import type { PuzzleData } from '../types';
import './PlaySetupScreen.css';

const COUNT_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 3, label: '3' },
  { value: 5, label: '5' },
  { value: -1, label: '\u221e' },
];


export interface GameSettingsOverride {
  strict: boolean;
  hintCount: number;
  validateCount: number;
  autoMark: boolean;
  timerVisible: boolean;
  cellStateCycleEnabled: boolean;
  spanHintsEnabled: boolean;
}

interface PlaySetupScreenProps {
  puzzleName: string;
  subtitle: string;
  puzzle?: PuzzleData;
  progress?: CellState[][];
  onPlay: (settings: GameSettingsOverride, continueProgress: boolean) => void;
  onBack: () => void;
}

function ProgressPreview({ puzzle, grid }: { puzzle: PuzzleData; grid: CellState[][] }) {
  const clues = useMemo(() => cluesFromGrid(puzzle.solution), [puzzle]);
  const rowClues = clues.rows;
  const colClues = clues.cols;

  const maxRowClueLen = Math.max(...rowClues.map((r) => r.length));
  const maxColClueLen = Math.max(...colClues.map((c) => c.length));
  const totalCols = maxRowClueLen + puzzle.width;
  const totalRows = maxColClueLen + puzzle.height;
  const cellSize = Math.max(6, Math.min(22, Math.floor(220 / Math.max(totalCols, totalRows))));

  return (
    <BoardGrid
      size={puzzle.height}
      cols={puzzle.width}
      rowClues={rowClues}
      colClues={colClues}
      cellSize={cellSize}
      grid={grid}
      showCrosshair={false}
      tabIndex={-1}
    />
  );
}

export function PlaySetupScreen({
  puzzleName,
  subtitle,
  puzzle,
  progress,
  onPlay,
  onBack,
}: PlaySetupScreenProps) {
  const defaults = useGameSettings();

  const [strict, setStrict] = useState(defaults.strict);
  const [hintCount, setHintCount] = useState(defaults.hintCount);
  const [validateCount, setValidateCount] = useState(defaults.validateCount);
  const [autoMark, setAutoMark] = useState(defaults.autoMark);
  const [timerVisible, setTimerVisible] = useState(defaults.timerVisible);
  const [cellStateCycleEnabled, setCellStateCycleEnabled] = useState(
    defaults.cellStateCycleEnabled
  );
  const [spanHintsEnabled, setSpanHintsEnabled] = useState(
    defaults.spanHintsEnabled
  );
  const [continueProgress, setContinueProgress] = useState(true);

  const handlePlay = () => {
    playClick();
    onPlay(
      {
        strict,
        hintCount,
        validateCount,
        autoMark,
        timerVisible,
        cellStateCycleEnabled,
        spanHintsEnabled,
      },
      progress != null ? continueProgress : false,
    );
  };

  return (
    <div className="play-setup-screen">
      <ParallaxBg className="game-bg" />

      <Title className="play-setup-title">{puzzleName}</Title>
      <Muted as="p" className="play-setup-subtitle">
        {subtitle}
      </Muted>

      <div className="play-setup-body">
        <div className="play-setup-panel">
          <div className="play-setup-section">
            <div className="play-setup-section-header">Level Rules</div>

            <div className="play-setup-toggle-rows">
              <div className="play-setup-toggle-row">
                <div className="play-setup-toggle-info">
                  <span className="play-setup-toggle-label">Strict</span>
                  <span className="play-setup-toggle-desc">
                    Flag wrong fills and count mistakes
                  </span>
                </div>
                <Toggle
                  checked={strict}
                  onChange={() => {
                    playToggle();
                    setStrict((v) => !v);
                  }}
                />
              </div>
            </div>

            <div className="play-setup-count-rows">
              <div className="play-setup-count-row">
                <div className="play-setup-toggle-info">
                  <span className="play-setup-count-label">Hints</span>
                  <span className="play-setup-toggle-desc">
                    Reveal a selected cell
                  </span>
                </div>
                <MultiToggle
                  options={COUNT_OPTIONS}
                  value={hintCount}
                  onChange={(v) => {
                    playToggle();
                    setHintCount(v);
                  }}
                  label="Hints"
                />
              </div>
              <div className="play-setup-count-row">
                <div className="play-setup-toggle-info">
                  <span className="play-setup-count-label">Checks</span>
                  <span className="play-setup-toggle-desc">
                    Validate your progress
                  </span>
                </div>
                <MultiToggle
                  options={COUNT_OPTIONS}
                  value={validateCount}
                  onChange={(v) => {
                    playToggle();
                    setValidateCount(v);
                  }}
                  label="Checks"
                />
              </div>
            </div>
          </div>

          <Divider className="play-setup-divider" />

          <div className="play-setup-section">
            <div className="play-setup-section-header">Preferences</div>

            <div className="play-setup-toggle-rows">
              <div className="play-setup-toggle-row">
                <div className="play-setup-toggle-info">
                  <span className="play-setup-toggle-label">Show Timer</span>
                  <span className="play-setup-toggle-desc">
                    Display elapsed time
                  </span>
                </div>
                <Toggle
                  checked={timerVisible}
                  onChange={() => {
                    playToggle();
                    setTimerVisible((v) => !v);
                  }}
                />
              </div>
              <div className="play-setup-toggle-row">
                <div className="play-setup-toggle-info">
                  <span className="play-setup-toggle-label">
                    Cycle Cell States
                  </span>
                  <span className="play-setup-toggle-desc">
                    Tap cycles through states
                  </span>
                </div>
                <Toggle
                  checked={cellStateCycleEnabled}
                  onChange={() => {
                    playToggle();
                    setCellStateCycleEnabled((v) => !v);
                  }}
                />
              </div>
              <div className="play-setup-toggle-row">
                <div className="play-setup-toggle-info">
                  <span className="play-setup-toggle-label">Span Hints</span>
                  <span className="play-setup-toggle-desc">
                    Show space each clue needs
                  </span>
                </div>
                <Toggle
                  checked={spanHintsEnabled}
                  onChange={() => {
                    playToggle();
                    setSpanHintsEnabled((v) => !v);
                  }}
                />
              </div>
              <div className="play-setup-toggle-row">
                <div className="play-setup-toggle-info">
                  <span className="play-setup-toggle-label">Clue Progress</span>
                  <span className="play-setup-toggle-desc">
                    Gray out completed clues
                  </span>
                </div>
                <Toggle
                  checked={autoMark}
                  onChange={() => {
                    playToggle();
                    setAutoMark((v) => !v);
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {puzzle && (
          <div className="play-setup-preview-panel">
            <div className="play-setup-section-header play-setup-preview-label">
              Level Preview
            </div>
            <ProgressPreview
              puzzle={puzzle}
              grid={
                continueProgress && progress
                  ? progress
                  : Array.from({ length: puzzle.height }, () =>
                      Array<CellState>(puzzle.width).fill(CellState.Empty)
                    )
              }
            />
            {progress && (
              <div className="play-setup-toggle-row play-setup-preview-toggle-row">
                <div className="play-setup-toggle-info">
                  <span className="play-setup-toggle-label">Use shared progress</span>
                  <span className="play-setup-toggle-desc">
                    {continueProgress
                      ? 'Start where they left off'
                      : 'Start fresh'}
                  </span>
                </div>
                <Toggle
                  checked={continueProgress}
                  onChange={() => {
                    playToggle();
                    setContinueProgress((v) => !v);
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <Button
        variant="primary"
        size="wide"
        className="play-setup-play-btn"
        onClick={handlePlay}
      >
        Play
      </Button>
    </div>
  );
}
